import { useAsyncData, useState } from '#imports'
import { computed, ref } from 'vue'

import { getAsyncDataKey } from '@backmarket/nuxt-module-http-v2/getAsyncDataKey'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'

import { SALES_CUSTOMER_CARE_SCOPE } from '@/scopes/care-commons/utils/logger/salesCustomerCare/config'
import { useSalesCustomerCareLogger } from '@/scopes/care-commons/utils/logger/salesCustomerCare/useSalesCustomerCareLogger'
import type { Orderline } from '~/api/my-orders/types/OrderlineList.types'

export function useOrderlines(pageParams = 1) {
  const { logHttpError } = useSalesCustomerCareLogger()
  const $httpFetch = useHttpFetch()
  const orderlines = useState<Orderline[]>('orderlines', () => [])

  const page = ref<number>(pageParams)

  const fetchKey = computed(() =>
    getAsyncDataKey('useOrderlines', { page: page.value }),
  )

  const { data, error, status } = useAsyncData(
    fetchKey,
    () =>
      $httpFetch('/my-orders/orderlines', {
        query: {
          page: page.value,
        },
        retry: 3,
        onResponseError: ({ response }) => {
          if (response) {
            logHttpError({
              // eslint-disable-next-line no-underscore-dangle
              error: response._data,
              errorStatusCode: response.status,
              errorTitle: `${SALES_CUSTOMER_CARE_SCOPE.myOrders} Error when fetching orderlines on page ${page.value}`,
            })
          }
        },
      }),
    {
      lazy: true,
      watch: [page],
      transform: (newData) => {
        if (page.value === 1) {
          orderlines.value = newData.results
        } else {
          orderlines.value = [...orderlines.value, ...newData.results]
        }

        return newData
      },
    },
  )

  const loadMore = () => {
    page.value += 1
  }

  const canLoadMore = computed(() => !!data.value?.next)

  return {
    canLoadMore,
    error,
    data,
    loadMore,
    orderlines,
    status,
  }
}
